import { BlockStack, Box, Button, Card, Checkbox, Divider, DropZone, Grid, InlineGrid, InlineStack, ProgressBar, RadioButton, Text, TextField } from "@shopify/polaris";
import { ArrowLeftIcon } from "@shopify/polaris-icons";
import { addDoc, collection } from "firebase/firestore";
import { getDownloadURL, getStorage, ref, uploadBytesResumable } from "firebase/storage";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePartnerUp } from "../../providers/PartnerUpStoreProvider";
import { contentFormats, getContentFormat } from "../atoms";

const campaignTemplates = [
  {
    id: 1,
    title: 'Affiliate Only',
    thumbnail: 'https://assets.codepen.io/6060109/c-2.png'
  },
  {
    id: 2,
    title: 'Pay Per Post',
    thumbnail: 'https://assets.codepen.io/6060109/c-3.png'
  },
  {
    id: 3,
    title: 'Affiliate + Fixed Payment',
    thumbnail: 'https://assets.codepen.io/6060109/c-1.png'
  },
  {

    id: 4,
    title: 'Recurring Content',
    thumbnail: 'https://assets.codepen.io/6060109/c-4.png'
  }
]


const assetFileTypes = ['image/jpeg', 'application/pdf', 'image/png', 'video/mp4', 'text/plain', 'application/msword'];
const heroFileTypes = ['image/jpeg', 'image/png'];
const validImageTypes = ['image/jpeg', 'image/png'];
const maxFileSize = 1 * 1024 * 1024 * 1024; // 2 GB in bytes

export default function CreateCampaign({ onClose }) {

  const { store, db, refetchCampaigns } = usePartnerUp()
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [campaign, setCampaign] = useState({});
  const [createLoading, setCreateLoading] = useState(false);

  const formatsArray = Object.keys(campaign?.content?.formats || {}).map(k => {
    return { [k]: campaign?.content?.formats[k] }
  })

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileLoading, setUploadFileLoading] = useState(false);

  const handleDropZoneDrop = useCallback(
    (_dropFiles, acceptedFiles, _rejectedFiles) => {
      if (acceptedFiles[0].size > maxFileSize) {
        alert('File size exceeds the 1GB limit.');
        return
      }
      handleUpload(acceptedFiles[0])
    },
    [],
  );

  async function createCampaign() {
    try {
      setCreateLoading(true);
      await addDoc(collection(db, 'campaigns'), {
        ...campaign,
        shop: store?.id,
        archived: false,
        created: new Date()?.toUTCString(),
        updated: new Date()?.toUTCString(),
      })
      refetchCampaigns()
      onClose()
    } catch (error) {
      console.error(error)
      alert('Error creating campaign. Please try again.')
      setCreateLoading(false);
    } finally {
    }
  }

  const handleUpload = (selectedFile) => {
    setUploadFileLoading(true);

    const fileRef = ref(getStorage(), `${store?.shop}/campaignHero/${selectedFile?.name}`);
    const uploadTask = uploadBytesResumable(fileRef, selectedFile);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        // Get the progress percentage
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (error) => {
        console.error(error)
        setUploadFileLoading(false);
        alert('Error uploading file. Please try again.');
        /*
          setToast({
            content: 'Error uploading file. Please try again.',
            error: true,
          })
        */
      },
      () => {
        setUploadFileLoading(false);
        // File uploaded successfully
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          setCampaign({ hero: downloadURL });
        })
        /*
        setToast({
          content: 'Asset uploaded successfully.',
        })
*/
        setUploadProgress(0);
      }
    );
  };

  const uploadedFiles = selectedFile ? (
    <Box padding="5">
      <LegacyStack vertical>
        <VerticalStack gap="2" inlineAlign="center">
          {
            selectedFile ? (
              <Thumbnail
                size="small"
                alt={selectedFile.name}
                source={
                  validImageTypes.includes(selectedFile.type)
                    ? window.URL.createObjectURL(selectedFile)
                    : NoteMinor
                }
              />
            ) : null
          }
          <Text variant="bodySm">
            {selectedFile.name}
          </Text>
          <Text variant="bodyMd" as="p">
            {(selectedFile.size / 1000000).toFixed(1)} MB
          </Text>
        </VerticalStack>
      </LegacyStack>
    </Box>
  ) : null;

  const steps = [
    (
      <BlockStack gap="500" inlineAlign="center">
        <Text variant="headingLg">How It Works</Text>
        <Card>
          <BlockStack gap="100" inlineAlign="center">
            <Text variant="headingSm">
              New Contract Template
            </Text>
            <Text variant="bodyLg">
              Let's Design a Contract, You can customize it later per-creator
            </Text>
          </BlockStack>
        </Card>
        <InlineStack align="space-between">
          <Button primary onClick={() => {
            setStep(step + 1)
          }}>Start ⚡</Button>
        </InlineStack>
      </BlockStack>
    ),
    (
      <BlockStack gap="500" inlineAlign="center">
        <Text variant="headingLg">Select a Starting Point</Text>
        <InlineGrid columns="4" gap="500">
          {
            campaignTemplates.map(template => (
              <Card>
                <BlockStack inlineAlign="center" key={template.id} gap="500">
                  <img style={{ width: '100%' }} src={template.thumbnail} alt={template.title} />
                  <Button onClick={() => {
                    if (template.id === 1) {
                      setCampaign({
                        campaignTitle: 'Affiliate Partner Program',
                        affiliateFields: {
                          commission: 10,
                          cookieDuration: 30,
                          defaultConversionValue: 100
                        },
                        content: {
                          formats: {},
                          baseType: 'evergreen'
                        },
                        payment: {
                          includeAffiliate: true
                        },
                      })
                    } else if (template.id === 2) {
                      setCampaign({
                        campaignTitle: 'Partner with us on Posts',
                        content: {
                          formats: {},
                          baseType: 'fixed'
                        },
                        payment: {
                          value: 50,
                          includeAffiliate: false
                        }
                      })
                    } else if (template.id === 3) {
                      setCampaign({
                        campaignTitle: 'Lets Collaborate',
                        affiliateFields: {
                          commission: 10,
                          cookieDuration: 30,
                          defaultConversionValue: 100
                        },
                        content: {
                          formats: {},
                          baseType: 'fixed'
                        },
                        payment: {
                          includeAffiliate: true,
                          value: 50
                        }
                      })
                    } else {
                      setCampaign({
                        campaignTitle: 'VIP Program',
                        affiliateFields: {
                          commission: 10,
                          cookieDuration: 30,
                          defaultConversionValue: 100
                        },
                        content: {
                          formats: {},
                          baseType: 'recurring',
                        },
                        payment: {
                          includeAffiliate: true,
                          value: 50
                        }
                      })
                    }
                    setStep(step + 1)
                  }}>
                    <Text variant="headingMd">{template.title}</Text>
                  </Button>
                </BlockStack>
              </Card>
            ))
          }
        </InlineGrid>
      </BlockStack>
    ),
    (
      <BlockStack gap="500">
        <InlineStack align="center">
          <Text variant="headingLg">Overview</Text>
        </InlineStack>
        <InlineGrid columns="1fr 2fr 1fr" gap="500">
          <Box />
          <BlockStack gap="500">
            <Card>
              <BlockStack gap="500">
                <TextField label="Title" value={campaign?.campaignTitle} onChange={v => setCampaign({ ...campaign, campaignTitle: v })} />
                <TextField multiline={2} label="Campaign Description" value={campaign?.campaignDescription} onChange={v => setCampaign({ ...campaign, campaignDescription: v })} />
                <BlockStack gap="100">
                  <Text>Hero Image</Text>
                  <BlockStack gap="200">
                    {
                      campaign?.hero ? (
                        <img style={{ width: '100%' }} src={campaign?.hero} alt="Hero" />
                      ) : (

                        <DropZone
                          allowMultiple={false}
                          accept={heroFileTypes.join(',')}
                          onDrop={handleDropZoneDrop}
                        >
                          {
                            selectedFile ? (
                              uploadedFiles
                            ) : (
                              <DropZone.FileUpload actionTitle={"Add Hero"}
                                actionHint={"Max Size 5MB"} />
                            )
                          }
                        </DropZone>
                      )
                    }
                    {/*
<input type="file" onChange={handleFileChange} />
*/}
                    <InlineStack align="center">
                      {uploadProgress > 0 && <Text>Upload Progress: {uploadProgress.toFixed(2)}%</Text>}
                    </InlineStack>
                  </BlockStack>
                </BlockStack>
              </BlockStack>
            </Card>
            <InlineStack align="space-between">
              <Button onClick={() => setStep(step - 1)}>Back</Button>
              <Button primary onClick={() => {
                setStep(step + 1)
              }}>Next</Button>
            </InlineStack>
          </BlockStack>
        </InlineGrid>
      </BlockStack>
    ),
    (
      <BlockStack gap="500">
        <InlineStack align="center">
          <Text variant="headingLg">Content</Text>
        </InlineStack>
        <InlineGrid columns="1fr 2fr 1fr" gap="500">
          <Box />
          <BlockStack gap="500">
            <Card>
              <BlockStack gap="300">
                <Text>Delivery</Text>
                <Divider />
                <InlineGrid columns="3" gap="500">
                  <InlineStack align="center">
                    <RadioButton label="Evergreen" checked={campaign?.content?.baseType === 'evergreen'} onChange={() => setCampaign({ ...campaign, content: { ...campaign.content, baseType: 'evergreen' } })} />
                  </InlineStack>
                  <InlineStack align="center">
                    <RadioButton label="One-Off" checked={campaign?.content?.baseType === 'fixed'} onChange={() => setCampaign({ ...campaign, content: { ...campaign.content, baseType: 'fixed' } })} />
                  </InlineStack>
                  <InlineStack align="center">
                    <RadioButton label="Recurring" checked={campaign?.content?.baseType === 'recurring'} onChange={() => setCampaign({ ...campaign, content: { ...campaign.content, baseType: 'recurring' } })} />
                  </InlineStack>
                </InlineGrid>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="300">
                <Text>Types</Text>
                <Divider />

                <InlineGrid gap="300" columns="3">
                  {
                    contentFormats.map(format => (
                      <BlockStack gap="100">
                        <Checkbox key={format.id} label={
                          <InlineStack gap="1">
                            {
                              format?.icon
                            }
                            <Text>{format?.label}</Text>
                          </InlineStack>
                        } checked={campaign?.content?.formats[format.value] !== undefined} onChange={() => {
                          if (campaign?.content?.formats[format.value]) {
                            const newFormats = { ...campaign?.content?.formats }
                            delete newFormats[format.value]
                            setCampaign({ ...campaign, content: { ...campaign.content, formats: newFormats } })
                          } else {
                            const newFormats = { ...campaign?.content?.formats }
                            newFormats[format.value] = 1
                            setCampaign({ ...campaign, content: { ...campaign.content, formats: newFormats } })
                          }
                        }} />
                        {
                          (campaign?.content?.formats[format.value] !== undefined && campaign?.content?.baseType !== 'evergreen') ? (
                            <TextField prefix="Quantity" value={campaign?.content?.formats[format.value]} onChange={v => {
                              const newFormats = { ...campaign?.content?.formats }
                              newFormats[format.value] = v
                              setCampaign({ ...campaign, content: { ...campaign.content, formats: newFormats } })
                            }
                            } />
                          ) : null
                        }
                      </BlockStack>
                    ))
                  }
                </InlineGrid>

              </BlockStack>
            </Card>
            {
              campaign?.content?.baseType === 'fixed' ? (
                <Card>
                  <BlockStack gap="300">
                    <Text>Due Date</Text>
                    <Divider />
                    <InlineGrid columns="1fr 1fr" gap="500">
                      <TextField type="date" value={campaign?.content?.deadline} onChange={v => setCampaign({ ...campaign, content: { ...campaign.content, deadline: v } })} />
                    </InlineGrid>
                  </BlockStack>
                </Card>
              ) : campaign?.content?.baseType === 'recurring' ? (
                <Card>
                  <BlockStack gap="300">
                    <Text>Recurs Every</Text>
                    <Divider />
                    <InlineGrid columns="1fr 1fr" gap="500">
                      <TextField value={campaign?.content?.deliveryDays} type="number" onChange={v => setCampaign({ ...campaign, content: { ...campaign.content, deadline: v } })} suffix="Days" />
                    </InlineGrid>
                  </BlockStack>
                </Card>
              ) : null
            }
            <InlineStack align="space-between">
              <Button onClick={() => setStep(step - 1)}>Back</Button>
              <Button primary onClick={() => {
                setStep(step + 1)
              }}>Next</Button>
            </InlineStack>
          </BlockStack>
        </InlineGrid>
      </BlockStack>
    ),
    (
      <BlockStack gap="500">
        <InlineStack align="center">
          <Text variant="headingLg">Specs</Text>
        </InlineStack>
        <InlineGrid columns="1fr 2fr 1fr" gap="500">
          <Box />
          <BlockStack gap="500">
            <Card>
              <TextField multiline={2} value={campaign?.specs?.script} label="Do's & Don'ts" onChange={v => {
                setCampaign({
                  ...campaign, specs: {
                    ...campaign.specs,
                    script: v
                  }
                })
              }} />
            </Card>
            <Card>
              <TextField multiline={2} value={campaign?.specs?.creativeDirection} label="Creative Direction" onChange={v => {
                setCampaign({
                  ...campaign, specs: {
                    ...campaign.specs,
                    creativeDirection: v
                  }
                })
              }} />
            </Card>
            <Card>
              <TextField multiline={2} value={campaign?.specs?.inspiration} label="Inspiration Examples" onChange={v => {
                setCampaign({
                  ...campaign, specs: {
                    ...campaign.specs,
                    inspiration: v
                  }
                })
              }} />
            </Card>
            <Card>
              <TextField multiline={2} value={campaign?.contentRights?.terms} label="Content Rights" onChange={v => {
                setCampaign({
                  ...campaign, contentRights: {
                    ...campaign.contentRights,
                    terms: v
                  }
                })
              }} />
            </Card>
            <Card>
              <BlockStack gap="300">
                <Text>Assets</Text>
                <Divider />
                <DropZone />
              </BlockStack>
            </Card>
            <InlineStack align="space-between">
              <Button onClick={() => setStep(step - 1)}>Back</Button>
              <Button primary onClick={() => {
                setStep(step + 1)
              }}>Next</Button>
            </InlineStack>
          </BlockStack>
        </InlineGrid>
      </BlockStack>
    ),
    (
      <BlockStack gap="500">
        <InlineStack align="center">
          <Text variant="headingLg">Payment</Text>
        </InlineStack>
        <InlineGrid columns="1fr 2fr 1fr" gap="500">
          <Box />
          <BlockStack gap="500">
            <Card>
              <BlockStack gap="300">
                <InlineStack gap="200">
                  <Checkbox checked={campaign?.payments?.includeAffiliate} onChange={v => {
                    setCampaign({
                      ...campaign,
                      payment: {
                        ...campaign.payment,
                        includeAffiliate: v
                      },
                      affiliateFields: {
                        commission: 10,
                        cookieDuration: 30,
                        defaultConversionValue: 100
                      }
                    })
                  }} />
                  <Text variant="bodyLg">Affiliate</Text>
                </InlineStack>
                {
                  campaign?.payments?.includeAffiliate ? (
                    <InlineStack columns="2" gap="200">
                      <TextField type="number" value={campaign?.affiliateFields?.commission} onChange={v => {
                        setCampaign({
                          ...campaign, affiliateFields: {
                            ...campaign.affiliateFields,
                            commission: v
                          }
                        })
                      }} label="Commission" suffix="%" />
                      <TextField type="number" value={campaign?.affiliateFields?.cookieDuration} onChange={v => {
                        setCampaign({
                          ...campaign, affiliateFields: {
                            ...campaign.affiliateFields,
                            cookieDuration: v
                          }
                        })
                      }} label="Cookie Duration" suffix="Days" />
                      <TextField type="number" value={campaign?.affiliateFields?.defaultConversionValue} onChange={v => {
                        setCampaign({
                          ...campaign, affiliateFields: {
                            ...campaign.affiliateFields,
                            defaultConversionValue: v
                          }
                        })
                      }} label="Default Conversion Value" prefix="$" />
                    </InlineStack>
                  ) : null
                }
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="300">
                <InlineStack gap="200">
                  <Checkbox checked={campaign?.payment?.value !== undefined} onChange={v => {
                    if (!v) {
                      const newPayment = { ...campaign.payment }
                      delete newPayment.value
                      setCampaign({
                        ...campaign, payment: newPayment
                      })
                    } else {
                      setCampaign({
                        ...campaign, payment: {
                          ...campaign.payment,
                          value: 50
                        }
                      })
                    }
                  }} />
                  <Text variant="bodyLg">Fixed Payment</Text>
                </InlineStack>
                {
                  campaign?.payment?.value !== undefined ? (
                    <TextField label="Pay" type="number" value={campaign?.payment?.value} onChange={v => {
                      setCampaign({
                        ...campaign, payment: {
                          ...campaign.payment,
                          value: v
                        }
                      })
                    }} prefix="$" suffix="On Full Delivery" />
                  ) : null
                }
              </BlockStack>
            </Card>
            <InlineStack align="space-between">
              <Button onClick={() => setStep(step - 1)}>Back</Button>
              <Button primary onClick={() => {
                setStep(step + 1)
              }}>Next</Button>
            </InlineStack>
          </BlockStack>
        </InlineGrid>
      </BlockStack>
    ),
    (
      <BlockStack gap="500">
        <InlineStack align="center">
          <Text variant="headingLg">Summary</Text>
        </InlineStack>
        <InlineGrid columns="1fr 2fr 1fr" gap="500">
          <Box />
          <BlockStack gap="500">
            <Card>
              <Text variant="bodyLg">Fixed</Text>
            </Card>
            <Card>
              <Text variant="bodyLg">Affiliate</Text>
            </Card>
            <InlineStack align="space-between">
              <Button onClick={() => setStep(step - 1)}>Back</Button>
              <Button loading={createLoading} variant="primary" onClick={() => {
                createCampaign()
              }}>Create Contract</Button>
            </InlineStack>
          </BlockStack>
        </InlineGrid>
      </BlockStack>
    ),
  ]

  return (
    <BlockStack gap="500">
      <InlineStack gap="300">
        <Button icon={ArrowLeftIcon} onClick={onClose} />
        <Text variant="headingXl">New Contract</Text>
      </InlineStack>
      <ProgressBar progress={((step) / steps?.length) * 100} size="large" />
      {steps[step]}
    </BlockStack>
  )
}
